import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/features/auth/AuthContext';
import { CompanyProvider } from '@/features/company/CompanyContext';
import { TenantProvider } from '@/context/tenant-context';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { ToastProvider } from '@/components/Toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Layout + public routes — eagerly loaded (needed immediately)
import Layout from '@/routes/_layout';
import LoginPage from '@/routes/login';
import AuthCallbackPage from '@/routes/auth-callback';

// Protected routes — lazy loaded (code-split per route)
const DashboardPage = lazy(() => import('@/routes/dashboard'));
const SearchPage = lazy(() => import('@/routes/search/index'));
const SearchResultsPage = lazy(() => import('@/routes/search/results'));
const CasesPage = lazy(() => import('@/routes/cases/index'));
const CaseWorkbenchPage = lazy(() => import('@/routes/cases/[caseId]'));
const AlertsPage = lazy(() => import('@/routes/alerts/index'));
const AlertDetailPage = lazy(() => import('@/routes/alerts/[caseId]'));
const LookupPage = lazy(() => import('@/routes/lookup/index'));
const WebhooksPage = lazy(() => import('@/routes/operations/webhooks'));
const InboundPage = lazy(() => import('@/routes/operations/inbound'));
const IngestionPage = lazy(() => import('@/routes/operations/ingestion'));
const IntegrationsPage = lazy(() => import('@/routes/settings/integrations'));
const ActionSettingsPage = lazy(() => import('@/routes/settings/actions'));
const AccountPage = lazy(() => import('@/routes/settings/account'));
const TeamPage = lazy(() => import('@/routes/settings/team'));
const AuditLogPage = lazy(() => import('@/routes/settings/audit'));
const AdminPage = lazy(() => import('@/routes/admin/index'));
const ReviewQueuePage = lazy(() => import('@/routes/cases/review'));
const InvitePage = lazy(() => import('@/routes/invite'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
          <CompanyProvider>
            <ErrorBoundary>
            <Routes>
              {/* Public routes */}
              <Route
                path="/login"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <LoginPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route
                path="/invite/:token"
                element={
                  <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" /></div>}>
                    <InvitePage />
                  </Suspense>
                }
              />

              {/* Protected routes — wrapped in Layout shell */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" /></div>}>
                      <Layout />
                    </Suspense>
                  </ProtectedRoute>
                }
              >
                {/* Dashboard */}
                <Route index element={<DashboardPage />} />

                {/* Transaction Search */}
                <Route path="search" element={<SearchPage />} />
                <Route path="search/results" element={<SearchResultsPage />} />

                {/* Dispute Cases */}
                <Route path="cases" element={<CasesPage />} />
                <Route path="cases/review" element={<ReviewQueuePage />} />
                <Route path="cases/:caseId" element={<CaseWorkbenchPage />} />

                {/* Alerts */}
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="alerts/:caseId" element={<AlertDetailPage />} />

                {/* Manual Lookup */}
                <Route path="lookup" element={<LookupPage />} />

                {/* Operations */}
                <Route path="operations/webhooks" element={<WebhooksPage />} />
                <Route path="operations/inbound" element={<InboundPage />} />
                <Route path="operations/ingestion" element={<IngestionPage />} />

                {/* Settings */}
                <Route path="settings/integrations" element={<ProtectedRoute minRole="manager"><IntegrationsPage /></ProtectedRoute>} />
                <Route path="settings/actions" element={<ProtectedRoute minRole="manager"><ActionSettingsPage /></ProtectedRoute>} />
                <Route path="settings/audit" element={<ProtectedRoute minRole="manager"><AuditLogPage /></ProtectedRoute>} />
                <Route path="settings/account" element={<AccountPage />} />
                <Route
                  path="settings/team"
                  element={
                    <ProtectedRoute minRole="manager">
                      <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" /></div>}>
                        <TeamPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />

                {/* Admin — Kombatix staff only.
                    This route was ungated while every sensitive route around it
                    carries a ProtectedRoute. Gating only the sidebar link is not
                    a gate: the page renders for anyone who types the URL. */}
                <Route
                  path="admin"
                  element={
                    <ProtectedRoute requirePlatformAdmin>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />

                {/* 404 catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
            </ErrorBoundary>
          </CompanyProvider>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
